import type { NeedCategory } from '../types/api'

/**
 * Display labels only.
 *
 * The option LISTS that used to live in this file (subjects, levels, modes, budgets) are gone:
 * they had drifted out of the server's taxonomy and were offering users values the server
 * rejects - "Painting", "Coding", "Prompt Engineering", "English", "Piano / Keyboard". Anything
 * a user can pick and submit must come from GET /api/taxonomy via `useTaxonomy()`.
 *
 * These labels survive only as a synchronous fallback for read-only rendering (a saved need's
 * category shown in a list) where waiting on a fetch would be silly. They never feed an input,
 * so they cannot produce an unmatchable value.
 */
export const NEED_CATEGORY_LABELS: Record<NeedCategory, string> = {
  ACADEMICS: 'Academics (School Subjects)',
  COMPETITIVE_EXAMS: 'Competitive Exams',
  MUSIC: 'Music',
  SPORTS_FITNESS: 'Health, Sports & Fitness',
  ARTS_CREATIVITY: 'Arts & Creativity',
  SKILLS_TECHNOLOGY: 'Skills & Technology',
  AI_FLUENCY: 'AI Fluency',
  LANGUAGES: 'Languages',
  // Byte-identical to the server's Taxonomy labels, since these strings are the match key, not
  // decoration - see the note above about drift.
  SCHOOL_ADMISSION: 'School Admission',
  HIGHER_EDUCATION: 'Higher Education',
  INTERNSHIPS: 'Internships',
  ENTREPRENEURSHIP: 'Entrepreneurship',
}

export const NEED_CATEGORIES = Object.keys(NEED_CATEGORY_LABELS) as NeedCategory[]

/**
 * A service's type, the same way the server derives {@code Participant.serviceType} in
 * NeedProjection.applyServices - byte-identical to that mapping, not a separate decision. It is
 * never asked for and never sent anywhere: a category picks it, so showing it beside the category
 * that produced it (see ServicesSection) is the whole of what it is for.
 */
export function serviceTypeFor(category: NeedCategory): string {
  if (category === 'ACADEMICS') return 'School Tutoring'
  if (category === 'COMPETITIVE_EXAMS') return 'Test Prep'
  return 'Other'
}

/** Renders a jsonb field (items/timing/detail) as a short human-readable string. */
export function summarizeJson(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map((v) => summarizeJson(v)).filter(Boolean).join(', ')
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).map((v) => summarizeJson(v)).filter(Boolean).join(', ')
  }
  return String(value)
}

/** Splits a comma-separated string into a JSON array of trimmed, non-empty strings. */
export function parseCommaList(text: string): string[] {
  return text.split(',').map((s) => s.trim()).filter(Boolean)
}

/** Reads a jsonb field back as a string array (for pre-filling multi-select chip state). */
export function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string')
  return []
}

/** Reads a single string field out of a jsonb "detail" object, e.g. { class, board }. */
export function readDetailField(detail: unknown, key: string): string {
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const value = (detail as Record<string, unknown>)[key]
    return typeof value === 'string' ? value : ''
  }
  return ''
}
